import { Button } from '@mui/material';
import * as Sentry from '@sentry/react';
import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { isNotNullOrUndefined, raise } from '@vy/utils';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCreatePlanContext } from '../../../../contexts/create-plan-context';
import { useFormErrorNotificationContext } from '../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import { PLAN_CREATE_ROUTE } from '../../../../router/routing-consts';
import type { StepsCtaLabels } from '../create-plan-page-consts/create-plan-default-values';
import { getCreatePlanDefaultValues } from '../create-plan-page-consts/create-plan-default-values';
import type { PlanInputForm } from './CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from './CreatePlanPageTabs/create-plan-page-types/create-plan-tab';

type StepperNavigatorProps = {
  stepsLabels: StepsCtaLabels;
  formClearable: boolean;
};

export const CreatePlanPageStepperNavigator: React.FC<StepperNavigatorProps> = ({
  stepsLabels,
  formClearable,
}) => {
  const {
    planCreationStepper: { currentStep, nextStep, onFinalStep, isFinalStep },
    onFormSubmission,
  } = useCreatePlanContext();
  const { setShowErrorNotification } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const {
    trigger,
    reset,
    getValues,
    resetField,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useFormContext();
  const navigate = useVyNavigate();

  const handleFailedFormSubmission = () => {
    console.error('Form is invalid:', errors);
    Sentry.captureException(new Error(`Create Plan Form is invalid: ${errors}`));
    setShowErrorNotification(true);

    return undefined;
  };

  const handleClick = useCallback(async () => {
    const validForm = await trigger();
    if (!validForm) {
      return handleFailedFormSubmission();
    }

    if (isFinalStep) {
      if (isNotNullOrUndefined(onFinalStep)) {
        return onFinalStep();
      }

      if (isNotNullOrUndefined(onFormSubmission)) {
        const submitForm = handleSubmit(onFormSubmission, handleFailedFormSubmission);

        return submitForm();
      }

      raise('onFinalStep and onFormSubmission are both undefined');
    }

    return nextStep();
  }, [isFinalStep, trigger, nextStep, onFinalStep, onFormSubmission, errors, setShowErrorNotification]);

  const handleClearForm = useCallback(() => {
    const { customerId, startDate, contractType } = getValues();
    reset(
      getCreatePlanDefaultValues({
        startDate,
        customerId,
        contractType,
      }),
      {
        keepDefaultValues: true,
      },
    );

    resetField('name');
    navigate(PLAN_CREATE_ROUTE);
  }, [reset, resetField, navigate]);

  return (
    <div className="flex justify-end gap-x-4 border-t border-t-[#0D1B271F] px-6 py-4">
      {formClearable && (
        <Button
          variant="text"
          color="primary"
          size="medium"
          disableElevation
          disabled={isSubmitting}
          onClick={handleClearForm}
          {...getSelectorAttribute('plans-clear-form-btn')}
        >
          Clear form
        </Button>
      )}

      <Button
        variant="contained"
        color="primary"
        size="medium"
        disableElevation
        disabled={isSubmitting}
        onClick={handleClick}
        {...getSelectorAttribute(createPlanPageSelectors.stepper.submitFormButton)}
      >
        {stepsLabels[currentStep]}
      </Button>
    </div>
  );
};
